import { HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { STORAGE_KEYS } from '../../config/storage-keys.config';
import { StorageService } from '../../services/storage/storage.service';
import { OnboardingSessionService } from '../../../modules/onboarding/services/onboarding-session.service';
import { isPublicApiUrl } from '../../utils/public-route.util';

const BYPASS_URLS = [
  '/v1/auth/channel-partner/login',
  '/v1/auth/channel-partner/login-via-otp',
  '/v1/auth/refresh',
  '/v1/channel-partner/auth/signup',
  '/v1/channel-partner/auth/complete',
  '/v1/channel-partner/auth/email-otp/request',
  '/v1/channel-partner/auth/email-otp/verify',
  '/v1/auth/email-otp/request',
  '/v1/auth/email-otp/verify',
];

/** APIs that belong to the onboarding wizard session. */
const ONBOARDING_API_URLS = [
  '/v1/channel-partner/onboarding/',
  '/v1/channel-partners/verify-pan',
  '/v1/channel-partners/verify-gst',
  // Document verification during self-service registration (PAN / GST / RERA / Aadhaar).
  '/v1/integrations/surepass/',
  '/v1/integrations/idp/',
];

function isOnboardingApi(url: string): boolean {
  return ONBOARDING_API_URLS.some(fragment => url.includes(fragment));
}

function resolveAuthToken(params: {
  onboardingApi: boolean;
  portalToken: string | null;
  onboardingToken: string | null;
  onboardingSessionActive: boolean;
}): string | null {
  const { onboardingApi, portalToken, onboardingToken, onboardingSessionActive } =
    params;

  if (onboardingApi) {
    return onboardingToken ?? portalToken;
  }

  if (portalToken) {
    return portalToken;
  }

  if (onboardingSessionActive) {
    return onboardingToken;
  }

  return null;
}

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const storageService = inject(StorageService);
  const onboardingSession = inject(OnboardingSessionService);
  onboardingSession.ensureMigratedFromLegacySharedSession();

  const isBypassed =
    BYPASS_URLS.some(url => req.url.includes(url)) || isPublicApiUrl(req.url);
  if (isBypassed) {
    return next(req);
  }

  // Token refresh retry sets Authorization explicitly — do not overwrite it.
  if (req.headers.has('Authorization')) {
    return next(req);
  }

  const portalToken = storageService.getItem<string>(STORAGE_KEYS.AUTH_TOKEN);
  const onboardingToken = onboardingSession.getAccessToken();
  const onboardingApi = isOnboardingApi(req.url);

  const token = resolveAuthToken({
    onboardingApi,
    portalToken,
    onboardingToken,
    onboardingSessionActive: onboardingSession.isActive(),
  });

  if (token) {
    return next(
      req.clone({
        setHeaders: {
          Authorization: `Bearer ${token}`,
        },
      }),
    );
  }

  return next(req);
};
